/**
 * MonthPicker — minimalist dropdown to scroll through historical months.
 *
 * Generates months between `earliestYM` and current month (IST).
 * Stays small, focused. Used by DivisionDetail when periodView === 'monthly'.
 *
 * Returns the selected month as `YYYY-MM` string.
 */
import React, { useMemo } from 'react';

const MONTH_LABELS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// Generate list of YYYY-MM strings from `earliestYM` up to current IST month.
function buildMonthList(earliestYM = '2026-04') {
  const out = [];
  const today = new Date();
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const ist = new Date(today.getTime() + istOffsetMs);
  const curYM = `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, '0')}`;
  const [eY, eM] = earliestYM.split('-').map(Number);
  const [cY, cM] = curYM.split('-').map(Number);

  let y = eY, m = eM;
  // Safety cap: 24 months max
  for (let i = 0; i < 24; i++) {
    const ym = `${y}-${String(m).padStart(2,'0')}`;
    out.push(ym);
    if (y === cY && m === cM) break;
    m++;
    if (m > 12) { m = 1; y++; }
  }
  // Newest first
  return out.reverse();
}

function labelFor(ym) {
  if (!ym || !ym.includes('-')) return ym;
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_LABELS[m - 1]} ${y}`;
}

export default function MonthPicker({ value, onChange, earliestYM = '2026-04', testId = 'month-picker' }) {
  const months = useMemo(() => buildMonthList(earliestYM), [earliestYM]);

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="px-3 py-1 rounded-full text-xs cursor-pointer transition-all"
      style={{
        background: '#FFFFFF',
        color: '#1B2A4A',
        border: '0.5px solid #185FA5',
        outline: 'none',
      }}
    >
      {months.map((ym) => (
        <option key={ym} value={ym}>{labelFor(ym)}</option>
      ))}
    </select>
  );
}

// Helper exports (useful elsewhere)
export function monthBoundaries(ym) {
  if (!ym || !ym.includes('-')) return { from: null, to: null };
  const [y, m] = ym.split('-').map(Number);
  const from = `${y}-${String(m).padStart(2,'0')}-01`;
  // last day = 0th of next month
  const lastDay = new Date(y, m, 0).getDate();
  const to = `${y}-${String(m).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;
  return { from, to };
}

export function defaultCurrentYM() {
  const today = new Date();
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const ist = new Date(today.getTime() + istOffsetMs);
  return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2,'0')}`;
}
